'use client';

import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from 'hooks/dom/useFocusTrap';
import styles from './PickupCartSheet.module.css';

export type PickupCartSheetAction = {
  label: string;
  onClick: () => void;
  kind: 'primary' | 'secondary' | 'text';
  testId: string;
};

type Props = {
  title: string;
  body: string;
  /** Product titles shown under the body (already capped by the caller). */
  items?: string[];
  /** Line after the items ("and 2 more"). */
  more?: string;
  /** Two or three, in the order shown. */
  actions: PickupCartSheetAction[];
  /** Marks every action aria-disabled (and ignores clicks) while one is
   * running. Not `disabled`: a focused button that disables would drop focus
   * to <body> and leave the focus trap with nothing to hold. */
  busy?: boolean;
  /** Esc or the scrim: the safe choice (leave the cart as it is). Stable
   * (useCallback): the focus trap re-engages whenever it changes. */
  onClose: () => void;
};

/** A small decision dialog on the buy page when the cart and the Best Buy
 * pickup choice cannot share one order. Same mechanics and look as
 * BuyPageStoresModal: bottom sheet below 768px, centred card above; focus
 * starts on the title and returns to the control that opened it. */
export const PickupCartSheet = ({
  title,
  body,
  items,
  more,
  actions,
  busy = false,
  onClose,
}: Props) => {
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const pressedOnScrim = useRef(false);

  useFocusTrap(dialogRef, {
    onEscape: onClose,
    contain: true,
    initialFocus: () => titleRef.current,
  });

  // Body only: locking <html> too breaks the buy page's sticky gallery
  // (see components/shared/Modal.tsx).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return createPortal(
    <div
      className={styles.overlay}
      onPointerDown={(event) => {
        pressedOnScrim.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (pressedOnScrim.current && event.target === event.currentTarget) {
          onClose();
        }
        pressedOnScrim.current = false;
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        aria-busy={busy}
        data-testid="bb-cart-sheet"
      >
        <div className={styles.content}>
          <h2 className={styles.title} id={titleId} ref={titleRef} tabIndex={-1}>
            {title}
          </h2>
          <p className={styles.body} id={bodyId}>
            {body}
          </p>
          {items && items.length > 0 ? (
            <ul className={styles.items} data-testid="bb-sheet-items">
              {items.map((item, index) => (
                <li key={`${index}-${item}`} className={styles.item}>
                  {item}
                </li>
              ))}
              {more ? <li className={styles.more}>{more}</li> : null}
            </ul>
          ) : null}
        </div>
        <div className={styles.actions}>
          {actions.map((action) => (
            <button
              key={action.testId}
              type="button"
              className={`${styles.action} ${styles[action.kind]}`}
              onClick={() => {
                if (!busy) action.onClick();
              }}
              aria-disabled={busy ? 'true' : undefined}
              data-testid={action.testId}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};
